import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Sparkles, Star, User, MessageSquareText, Search, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const decisionStyle = {
  cast:    "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  reject:  "border-zinc-600 bg-zinc-800/40 text-zinc-400",
  pending: "border-amber-400/40 bg-amber-400/10 text-amber-300",
};

/**
 * Public read-only scoring dashboard for the audition team.
 * NO signup required. Access controlled by an unguessable share token.
 * Shows every auditioner + every scorer's individual acting/vocal/dance/comment.
 * Cannot score, cannot cast — read-only by design.
 */
export default function PublicAuditionScores() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [query, setQuery] = useState("");
  const [roundFilter, setRoundFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/api/public/audition-scores`, { params: { token } });
        setState({ loading: false, error: "", data: r.data });
      } catch (e) {
        setState({ loading: false, error: e.response?.data?.detail || "Could not load scores.", data: null });
      }
    })();
  }, [token]);

  const filtered = useMemo(() => {
    const signups = state.data?.signups || [];
    const q = query.trim().toLowerCase();
    return signups.filter((s) => {
      if (roundFilter !== "all" && s.round_id !== roundFilter && (roundFilter !== "none" || s.round_id)) return false;
      if (!q) return true;
      const hay = [s.name, ...(s.interested_roles || []), s.cast_character, s.round_name].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [state.data, query, roundFilter]);

  if (state.loading) {
    return (
      <Wrapper>
        <div className="text-zinc-400 text-sm tracking-widest uppercase text-center py-24">Loading scores…</div>
      </Wrapper>
    );
  }
  if (state.error) {
    return (
      <Wrapper>
        <div className="max-w-md mx-auto rounded-2xl border border-red-500/30 bg-red-500/5 p-8 text-center">
          <XCircle className="w-8 h-8 text-red-400 mx-auto" />
          <h1 className="mt-3 font-display text-2xl">Unavailable</h1>
          <p className="mt-2 text-sm text-zinc-300" data-testid="share-error">{state.error}</p>
        </div>
      </Wrapper>
    );
  }

  const { production, signups, rounds } = state.data;
  const counts = {
    total: signups.length,
    cast: signups.filter((s) => s.status === "cast").length,
    reject: signups.filter((s) => s.status === "reject").length,
    pending: signups.filter((s) => (s.status || "pending") === "pending").length,
  };

  return (
    <Wrapper>
      <header className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.32em] text-gold">View-only · Audition team</div>
        <h1 className="mt-1 font-display text-4xl md:text-5xl tracking-tight" data-testid="public-scores-title">
          {production.title || "Auditions"}
        </h1>
        <div className="mt-2 text-sm text-zinc-400">
          {production.theater_name && <span>{production.theater_name}</span>}
          {production.director && <span> · Dir. {production.director}</span>}
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <StatCard label="Auditioners" n={counts.total} tint="text-zinc-100" />
        <StatCard label="Pending" n={counts.pending} tint="text-amber-300" />
        <StatCard label="Cast" n={counts.cast} tint="text-emerald-300" />
        <StatCard label="Not cast" n={counts.reject} tint="text-zinc-400" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            data-testid="public-scores-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, character, round…"
            className="pl-9 bg-[#121212] border-white/10 h-11"
          />
        </div>
        <select
          data-testid="public-scores-round"
          value={roundFilter}
          onChange={(e) => setRoundFilter(e.target.value)}
          className="h-11 px-3 rounded border border-white/10 bg-[#121212] text-sm"
        >
          <option value="all">All rounds</option>
          <option value="none">Intake · no round</option>
          {rounds.map((r) => (<option key={r.id} value={r.id}>{r.name}</option>))}
        </select>
      </div>

      <div className="space-y-4">
        {filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-zinc-500 text-sm">
            No auditioners match your filters.
          </div>
        )}
        {filtered.map((a) => (
          <AuditionerCard key={a.id} a={a} />
        ))}
      </div>

      <footer className="mt-10 pt-6 border-t border-white/5 text-[11px] text-zinc-500 text-center">
        Shared privately for the audition team. Do not forward this link outside your production.
      </footer>
    </Wrapper>
  );
}

function AuditionerCard({ a }) {
  const status = a.status || "pending";
  const chip = decisionStyle[status] || decisionStyle.pending;
  return (
    <div
      data-testid={`aud-row-${a.id}`}
      className="rounded-2xl border border-white/10 bg-[#0F0F0F] overflow-hidden"
    >
      <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 border-b border-white/5">
        <Avatar url={a.headshot_url} name={a.name} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-xl truncate">{a.name}</div>
            <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${chip}`}>
              {status === "cast" ? (a.cast_character || "Cast") : status}
            </span>
            {a.round_name && (
              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-white/10 text-zinc-400">
                {a.round_name}
              </span>
            )}
          </div>
          {a.interested_roles?.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {a.interested_roles.map((r, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-400">{r}</span>
              ))}
            </div>
          )}
        </div>
        <ScorePill label="Acting" v={a.acting_avg} />
        <ScorePill label="Vocal"  v={a.vocal_avg} />
        <ScorePill label="Dance"  v={a.dance_avg} />
        <ScorePill label="Overall" v={a.overall_avg} highlight />
      </div>

      {a.scores?.length ? (
        <div className="divide-y divide-white/5">
          {a.scores.map((s) => (
            <div key={s.id} className="p-4 sm:p-5 flex flex-col sm:flex-row gap-4">
              <div className="w-40 shrink-0">
                <div className="flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-gold" />
                  <div className="text-sm font-medium text-zinc-100 truncate">{s.staff_name}</div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">{s.staff_role}</div>
                {s.round_name && (
                  <div className="text-[11px] text-zinc-500 mt-1">{s.round_name}</div>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                <ScoreCell label="Acting" v={s.acting_score} />
                <ScoreCell label="Vocal"  v={s.vocal_score} />
                <ScoreCell label="Dance"  v={s.dance_score} />
              </div>
              {s.comment && (
                <div className="flex-1 min-w-0 rounded-md border border-white/10 bg-[#0A0A0A] p-2.5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1.5">
                    <MessageSquareText className="w-3 h-3" /> Comment
                  </div>
                  <div className="mt-1 text-sm text-zinc-200 whitespace-pre-wrap break-words">{s.comment}</div>
                  {s.suggested_roles?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {s.suggested_roles.map((r, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-gold/10 text-gold border border-gold/30">Suggests {r}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="p-6 text-xs text-zinc-500 text-center">No scores yet for this auditioner.</div>
      )}
    </div>
  );
}

function Avatar({ url, name }) {
  const initials = (name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  return url ? (
    <img src={url} alt={name} className="w-14 h-14 rounded-full object-cover bg-[#121212] border border-white/10 shrink-0" />
  ) : (
    <div className="w-14 h-14 rounded-full bg-[#121212] border border-white/10 grid place-items-center text-sm font-medium text-zinc-400 shrink-0">
      {initials}
    </div>
  );
}

function ScorePill({ label, v, highlight }) {
  return (
    <div className={`rounded-md border px-2.5 py-1.5 min-w-[70px] text-center ${highlight ? "border-gold/40 bg-gold/5" : "border-white/10 bg-[#121212]"}`}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-lg ${highlight ? "text-gold" : "text-zinc-200"}`}>{v ?? "—"}</div>
    </div>
  );
}

function ScoreCell({ label, v }) {
  return (
    <div className="text-center">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-0.5 inline-flex items-center gap-1">
        <Star className="w-3 h-3 text-gold" />
        <span className="text-sm text-zinc-100 tabular-nums">{v ?? "—"}</span>
      </div>
    </div>
  );
}

function StatCard({ label, n, tint }) {
  return (
    <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-4">
      <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">{label}</div>
      <div className={`mt-1 text-3xl font-display ${tint}`}>{n}</div>
    </div>
  );
}

function Wrapper({ children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-gold" />
          <div>
            <div className="font-display text-lg leading-none">StageCall</div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mt-1">Scoring dashboard</div>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto p-6 md:p-10">{children}</main>
    </div>
  );
}
